import { UpdatedPropsIO } from "common/picker/reducer";

import {
  OnBlur,
  OnError,
  OnFocus,
  OnInput,
  OnRemove,
  OnSearch,
  OnSelect,
  OnStart,
} from "common/picker/action/types";
import { minimumInputCharSearch } from "common/picker/constant";
import { AnyAction } from "redux";

export interface ActionIO<TItems, TCriteria> {
  toCriteria: (terms: string) => TCriteria;
  search: (criteria: TCriteria) => Promise<TItems[]>;
  startItems: () => Promise<TItems[]>;
  updatedProps: () => UpdatedPropsIO;
  filterItems?: (items: TItems[]) => TItems[];
}

export interface Creators<TItem> {
  onBlur: () => OnBlur;
  onFocus: () => OnFocus;
  onInput: (
    terms: string
  ) => (dispatch: (action: AnyAction) => AnyAction) => Promise<any>;
  onRemove: (item: TItem) => OnRemove<TItem>;
  onSelect: (item: TItem) => OnSelect<TItem>;
  onStart: () => (
    dispatch: (action: OnStart<TItem> | OnError) => AnyAction
  ) => Promise<any>;
}

export const creators = <TItem, TCriteria>(
  io: ActionIO<TItem, TCriteria>
): Creators<TItem> => ({
  onBlur: (): OnBlur => ({ type: "ON_BLUR" }),

  onFocus: (): OnFocus => ({ type: "ON_FOCUS" }),

  onInput: (terms: string) => (dispatch: (action: AnyAction) => AnyAction) => {
    const criteria = io.toCriteria(terms);
    dispatch(onInputSync(terms, criteria));

    if (terms.length < minimumInputCharSearch) {
      return Promise.resolve([]).then(items => dispatch(onSearch(items)));
    }

    return io
      .search(criteria)
      .then(items => (io.filterItems ? io.filterItems(items) : items))
      .then(items => dispatch(onSearch(items)))
      .catch((reason: Error) => dispatch(onError(reason.message)));
  },

  onRemove: (item: TItem): OnRemove<TItem> => ({ type: "ON_REMOVE", item }),

  onSelect: (item: TItem): OnSelect<TItem> => ({ type: "ON_SELECT", item }),

  onStart: () => (
    dispatch: (action: OnStart<TItem> | OnError) => AnyAction
  ) => {
    return io
      .startItems()
      .then(items =>
        dispatch({
          type: "ON_START",
          items,
          updatedProps: io.updatedProps(),
        } as OnStart<TItem>)
      )
      .catch((reason: Error) => dispatch(onError(reason.message)));
  },
});

const onInputSync = <TCriteria>(
  terms: string,
  criteria: TCriteria
): OnInput<TCriteria> => ({
  type: "ON_INPUT",
  terms,
  criteria,
});

const onSearch = <T>(items: T[]): OnSearch<T> => ({ type: "ON_SEARCH", items });

const onError = (reason: string): OnError => ({ type: "ON_ERROR", reason });
